import { ContentItem } from '../shared/interfaces';

// Active chat scope for the positional persistence keys. The maps that keep
// the group's expansion/summary across remounts are SESSION-level; a purely
// positional key ("m1-r0") collides across chats — a brand-new chat's first
// answer briefly showed the PREVIOUS chat's sabiazinho label, and switching
// chats could restore a wrong label permanently. MessageList sets this every
// render (parent body runs before children), so ThinkingGroup can prefix it.
let thinkingScope = '';
export function setThinkingScope(chatId: string): void {
  thinkingScope = chatId || '';
}
export function getThinkingScope(): string {
  return thinkingScope;
}

/** One maximal run of consecutive thinking items inside a message. */
export interface ThinkingRun {
  /** Index in `content` of the run's first item (where the group renders). */
  startIndex: number;
  /** Index in `content` of the run's last item (inclusive). */
  endIndex: number;
  /** The run's reasoning / tool_action / code_execution items, in order. */
  items: ContentItem[];
  /** Ordinal of the run within the message (0 = first burst). Stable across
      content-index shifts, unlike startIndex. */
  ordinal: number;
}

const GROUPABLE = new Set(['reasoning', 'tool_action', 'code_execution', 'search_results', 'narration', 'activity_step']);

// Chat do Sabiá 5 (contas internas): o turno mostra só a linha de status e a
// resposta final. Módulo, como o thinkingScope: o MessageList liga a cada render.
let compactActivity = false;
export function setCompactActivity(on: boolean): void {
  compactActivity = on;
}
export function isCompactActivity(): boolean {
  return compactActivity;
}

// Enquanto o texto do ciclo não passa disto, ainda pode ser só uma frase antes
// de ferramentas ("Vou buscar as bulas…"): fica retido e o status continua.
// Atingiu o limite (ou o turno acabou), é a resposta — entra com streaming
// simulado. Um trecho liberado pelo tamanho não pode sumir na próxima etapa,
// inclusive quando o histórico é carregado de novo, sem estado do stream.
// ~150 tokens: a 80 tok/s, no máximo ~2 s de espera.
export const HOLD_ANSWER_CHARS = 600;

const isVisibleText = (it: any) => !!it && it.type === 'text' && !!((it.text || '') as string).trim();

// No modo compacto o checklist (lista_de_tarefas) é um card fixo abaixo do
// status: não quebra o grupo, não encerra o "Trabalhando" e não faz o texto
// antes dele virar resposta. O card vai sempre para o fim do conteúdo
// (applyTodoToHistory), então sem isso partia o status em dois.
const compactTransparent = (it: any) => compactActivity && !!it && it.type === 'todo_list';

// Equivalente a concatenar os fragmentos e medir trim().length, mas sem copiar
// a resposta inteira a cada delta. Espaços entre fragmentos contam; espaços
// nas pontas não. Para assim que o trecho atinge o limite de liberação.
function reachesAnswerLimit(content: ContentItem[], indices: number[]): boolean {
  let length = 0;
  let whitespace = 0;
  for (const index of indices) {
    const text = (content[index] as any).text || '';
    for (let i = 0; i < text.length; i++) {
      if (/\s/.test(text[i])) {
        if (length > 0) whitespace = Math.min(HOLD_ANSWER_CHARS, whitespace + 1);
      } else {
        length += whitespace + 1;
        whitespace = 0;
        if (length >= HOLD_ANSWER_CHARS) return true;
      }
    }
  }
  return false;
}

/**
 * Modo compacto: só trechos curtos seguidos de mais trabalho viram narração.
 * Trechos com pelo menos HOLD_ANSWER_CHARS permanecem visíveis, mesmo antes
 * de novas etapas. Blocos de texto consecutivos contam como um único trecho
 * (checklists são transparentes), tanto no stream quanto no histórico salvo.
 * Durante o stream, o trecho final ainda curto fica retido. Mesmo tamanho e
 * ordem que `content`: os índices continuam valendo. Não altera o original.
 */
export function compactAgentContent(content: ContentItem[], streaming: boolean): ContentItem[] {
  if (!compactActivity || !Array.isArray(content)) return content;
  let out: ContentItem[] | null = null;
  const replace = (i: number, item: any) => {
    if (!out) out = content.slice();
    out[i] = item;
  };
  let textIndices: number[] = [];
  const flushText = (next?: ContentItem) => {
    if (!reachesAnswerLimit(content, textIndices)) {
      const narration = !!next && GROUPABLE.has(next.type);
      const held = !next && streaming;
      if (narration || held) textIndices.forEach(i => {
        const item = content[i] as any;
        if (!isVisibleText(item)) return;
        replace(i, narration ? { type: 'narration', text: item.text } : { ...item, text: '' });
      });
    }
    textIndices = [];
  };
  for (let i = 0; i < content.length; i++) {
    const item = content[i];
    if (!item || compactTransparent(item)) continue;
    if (item.type === 'text') {
      textIndices.push(i);
    } else {
      flushText(item); // arquivos e cards encerram o trecho sem escondê-lo
    }
  }
  flushText();
  return out || content;
}

/**
 * Find every maximal run of consecutive reasoning / tool_action /
 * code_execution items in a message (empty text items are transparent — they
 * neither join nor break a run). A run becomes a collapsed "Pensando…/Pensou"
 * group when it contains reasoning or 2+ steps; a single bare tool call stays
 * inline, and visible answer text is never absorbed — it separates runs.
 *
 * This keeps a turn with a pre-answer burst AND mid-answer bursts (narration
 * text between tool batches) down to one expandable line per burst instead of
 * a pile of boxes.
 */
export function computeThinkingRuns(content: ContentItem[]): ThinkingRun[] {
  const runs: ThinkingRun[] = [];
  let start = -1;
  let end = -1;
  let items: ContentItem[] = [];

  const flush = () => {
    if (items.length === 0) return;
    const hasReasoning = items.some((it: any) => it.type === 'reasoning');
    // web_search emits NO tool_action (the search_results block IS the step),
    // so a reasoning-less search turn is a lone search_results item — it still
    // must collapse into the group instead of rendering as a bare box.
    const hasSearchResults = items.some((it: any) => it.type === 'search_results');
    // Etapa anunciada pelo servidor (chat compacto): já é o status do turno.
    const hasServerStep = items.some((it: any) => it.type === 'activity_step');
    if (hasReasoning || hasSearchResults || hasServerStep || items.length >= 2) {
      runs.push({ startIndex: start, endIndex: end, items, ordinal: runs.length });
    }
    start = -1;
    end = -1;
    items = [];
  };

  for (let i = 0; i < content.length; i++) {
    const it = content[i] as any;
    if (!it) {
      flush();
      continue;
    }
    if (it.type === 'text') {
      if (((it.text || '') as string).trim()) flush(); // visible text breaks the run
      continue; // empty text is transparent
    }
    if (compactTransparent(it)) continue;
    if (GROUPABLE.has(it.type)) {
      if (items.length === 0) start = i;
      end = i;
      items.push(it);
      continue;
    }
    flush(); // file_ref / input_file / message_composer etc. break the run
  }
  flush();
  return runs;
}

/**
 * Stable identity for a run, derived from its FIRST item's head. Used to keep
 * the group's expanded/collapsed state across the remount that happens when
 * the streaming subtree is swapped for the persisted-message subtree at the
 * end of generation. The first item's head is written once and never changes
 * as the stream appends, so the signature is stable for the whole turn.
 */
export function runSignature(items: ContentItem[]): string {
  const it = items[0] as any;
  if (!it) return '';
  const head =
    it.type === 'reasoning'
      ? (it.reasoning || '')
      : it.type === 'tool_action'
        ? ((it.queries || []).join(',') || it.label || '')
        : JSON.stringify(it.args || {});
  return `${it.type}:${String(head).slice(0, 80)}`;
}

/** Set of content indices swallowed by the given runs (skip in normal render). */
export function groupedIndexSet(runs: ThinkingRun[], content: ContentItem[]): Set<number> {
  const set = new Set<number>();
  for (const run of runs) {
    for (let i = run.startIndex; i <= run.endIndex; i++) {
      const it = content[i] as any;
      // Only the run's own items — a transparent empty-text item inside the
      // span keeps its (invisible) normal rendering.
      if (it && GROUPABLE.has(it.type)) set.add(i);
    }
  }
  return set;
}

/** Whether this run is the active progress indicator for the current turn. */
export function thinkingRunPending(
  content: ContentItem[], run: ThinkingRun, streaming: boolean, interrupted = false,
): boolean {
  // Native deltas and tool events may leave stale per-item pending flags.
  // Only the trailing run of a live turn can still be executing.
  return streaming && !interrupted && !hasVisibleItemsAfter(content, run.endIndex);
}

/**
 * True when anything VISIBLE renders after ``index`` — non-empty text or any
 * non-text item. Invisible empty-text items don't count: a trailing thinking
 * run followed only by an empty text item is still "the end of the message",
 * and the group must keep its "Pensando" streaming state through the gap
 * between the draft finishing and the answer text starting.
 */
export function hasVisibleItemsAfter(content: ContentItem[], index: number): boolean {
  for (let i = index + 1; i < content.length; i++) {
    const it = content[i] as any;
    if (!it) continue;
    if (it.type === 'text') {
      if (((it.text || '') as string).trim()) return true;
      continue;
    }
    if (it.type === 'search_results') continue; // mid-burst results: keep "Pensando"
    if (it.type === 'activity_step') continue; // rótulo do status, não aparece sozinho
    if (compactTransparent(it)) continue;
    return true;
  }
  return false;
}

/**
 * True when the message ends in a thinking run with nothing visible after it —
 * i.e. while generation is active, the ThinkingGroup is the live activity
 * indicator ("Pensando"), so no extra spinner should render.
 */
export function trailingThinkingRunActive(content: ContentItem[]): boolean {
  const runs = computeThinkingRuns(content);
  if (runs.length === 0) return false;
  return !hasVisibleItemsAfter(content, runs[runs.length - 1].endIndex);
}

/**
 * True when an assistant message has something for its row to render. That row
 * draws the turn's spinner itself, under its own content, so the list must not
 * add a second one below it (between cycles — text, then a tool — the streaming
 * buffer is empty and both used to render).
 */
export function assistantRowRendersContent(message: any): boolean {
  if (!message || message.role !== 'assistant') return false;
  const content = message.content;
  if (typeof content === 'string') return content !== '';
  return Array.isArray(content) && content.length > 0;
}

/**
 * Etapas do status compacto: cada ciclo do agente começa com o raciocínio, então
 * um item 'reasoning' depois de qualquer outra coisa abre uma etapa nova
 * (raciocínios seguidos ficam na mesma). Cada etapa vira uma linha do status.
 */
export function splitActivitySteps(items: ContentItem[]): ContentItem[][] {
  const steps: ContentItem[][] = [];
  let cur: ContentItem[] = [];
  for (const it of items as any[]) {
    if (!it) continue;
    if (it.type === 'reasoning' && cur.some((x: any) => x.type !== 'reasoning')) {
      steps.push(cur);
      cur = [];
    }
    cur.push(it);
  }
  if (cur.length) steps.push(cur);
  return steps;
}

/**
 * Evento `activity_step` do chat compacto ({index} ao começar uma etapa,
 * {index, label} quando o servidor escreve o rótulo) aplicado ao histórico.
 * Etapa nova vai para o fim — o texto curto antes dela vira narração, como
 * acontecia com o raciocínio —, e o rótulo atualiza a etapa no lugar. Sem
 * mensagem do assistente ainda (o raciocínio não vem mais), cria o scaffold.
 */
export function applyActivityStepToHistory<M extends { role: string; content: any }>(
  prev: M[], payload: { index?: unknown; label?: unknown },
): M[] {
  if (prev.length === 0 || typeof payload?.index !== 'number') return prev;
  const label = typeof payload.label === 'string' && payload.label ? payload.label : undefined;
  const lastEntry = prev[prev.length - 1];
  const isAssistant = lastEntry.role === 'assistant';
  const lastMsg: any = isAssistant ? { ...lastEntry } : { role: 'assistant', content: [], references: [] };
  const content: any[] = Array.isArray(lastMsg.content)
    ? [...lastMsg.content]
    : (lastMsg.content ? [{ type: 'text', text: lastMsg.content as string }] : []);
  const at = content.findIndex((it: any) => it?.type === 'activity_step' && it.index === payload.index);
  if (at >= 0) {
    if (!label || content[at].label === label) return prev;
    content[at] = { ...content[at], label };
  } else {
    content.push({ type: 'activity_step', index: payload.index, ...(label ? { label } : {}) });
  }
  const next = { ...lastMsg, content } as M;
  return isAssistant ? [...prev.slice(0, -1), next] : [...prev, next];
}
